import { Routes } from "@blitzjs/next"
import { invoke } from "@blitzjs/rpc"

import moment from "moment"
import useIsAdmin from "app/core/hooks/useIsAdmin"
import { useMutation, useQuery } from "@blitzjs/rpc"
import { Dialog } from "@headlessui/react"
import { SButton, SIcon, SIconType } from "@superwall-me/sui"
import getApplication from "app/applications/queries/getApplication"
import getApplicationsInOrg from "app/applications/queries/getApplicationsInOrg"
import ModalOverlay, { OpenCloseRender } from "app/core/components/layout/ModalOverlay"
import { useApplicationContext } from "app/core/contexts/ApplicationContext"
import { FORM_ERROR } from "app/paywalls/components/forms/DuplicatePaywallForm"
import getEditorRouteByPaywall from "app/paywalls/helpers/getEditorRouteByPaywall"
import useLocalizedSubstitutions from "app/paywalls/hooks/useLocalizedSubstitutions"
import usePaywallVariables from "app/paywalls/hooks/usePaywallVariables"
import duplicatePaywall from "app/paywalls/mutations/duplicatePaywall"
import getPaywall from "app/paywalls/queries/getPaywall"
import { CloneOptionType } from "app/paywalls/validations"
import { useRouter } from "next/router"
import { FC } from "react"
import { IoArrowForward } from "react-icons/io5"
import tw from "tailwind-styled-components"
import PaywallPreview from "../PaywallPreview"
import getPaywallPreviewToken from "app/paywalls/queries/getPaywallPreviewToken"
import { EditorServiceHelper, UnPromisify } from "@superwall-me/schema"
import { env } from "app/core/env"
import { TPaywallTemplates } from "app/paywalls/queries/getPaywallTemplates"

export const DuplicateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 16 16">
    <title>Duplicate</title>
    <g fill="currentColor">
      <path
        fill="currentColor"
        d="M11,12H1c-0.553,0-1-0.447-1-1V1c0-0.552,0.447-1,1-1h10c0.553,0,1,0.448,1,1v10C12,11.553,11.553,12,11,12z "
      ></path>{" "}
      <path data-color="color-2" d="M15,16H4v-2h10V4h2v11C16,15.553,15.553,16,15,16z"></path>
    </g>
  </svg>
)

const defaultRenderButton = ({ open }) => (
  <button onClick={open}>
    <DuplicateIcon />
  </button>
)

const DuplicatePaywallButton = tw.button`
text-left flex flex-row justify-between content-center items-center
pb-4
pt-4
border-t w-full p border-grayscale-lightest border-opacity-10
`

const getEditorUrl = (paywall: UnPromisify<ReturnType<typeof getPaywall>>) => {
  const editorURL = new EditorServiceHelper(env).editorURL({
    applicationId: paywall.applicationId,
    paywallId: paywall.id,
    version: "latest",
  })
  let url: URL
  try {
    url = new URL(editorURL)
  } catch (e) {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://superwall.com"
    url = new URL(origin + editorURL)
  }
  return url.toString()
}

const DropdownButtons = ({
  paywallId,
  name = "",
  cloneOptionType,
  showPreview = false,
}: {
  paywallId: number
  name?: string
  cloneOptionType: CloneOptionType
  showPreview?: boolean
}) => {
  const router = useRouter()
  const { applicationId } = useApplicationContext()

  const [paywall] = useQuery(getPaywall, { id: paywallId }, { suspense: false })

  const [duplicatePaywallMutation] = useMutation(duplicatePaywall)
  const [application] = useQuery(getApplication, { id: applicationId }, { suspense: false })
  const [applications] = useQuery(
    getApplicationsInOrg,
    {
      organizationId: application?.organizationId!,
    },
    { suspense: false, enabled: !!application }
  )
  const filteredApplications = (applications || []).filter((app) => app.id !== applicationId)
  const onlyOneApplication = filteredApplications.length === 0

  const isCloningAsTemplate = cloneOptionType === CloneOptionType.AsTemplate
  const isAdminUser = useIsAdmin()

  console.log("------------------")
  console.log("showPreview", showPreview)
  console.log("paywall", paywall)
  console.log("cloneOptionType", cloneOptionType)
  console.log("------------------")

  const isAdmin = useIsAdmin()

  if (
    showPreview &&
    paywall &&
    (onlyOneApplication || cloneOptionType === CloneOptionType.AsTemplate)
  ) {
    return (
      <div className="flex flex-col items-center gap-5">
        <h2 className="opacity-70 text-center -mt-1">
          {paywall.name} {isCloningAsTemplate || "(Copy)"}
        </h2>
        <div className="flex flex-col w-full items-center justify-center gap-3">
          <PaywallContainer paywall={paywall}></PaywallContainer>
          <span className="text-white text-sm opacity-50 tracking-loose">
            Last updated {moment(paywall.updatedAt).fromNow()}
          </span>
        </div>

        <div
          className={`${
            isAdmin && isCloningAsTemplate ? "grid grid-cols-[auto,1fr] gap-2" : "grid grid-cols-1"
          } my-3 w-[250px]`}
        >
          {isAdmin && isCloningAsTemplate ? (
            <SButton
              href={getEditorUrl(paywall)}
              variant="primary"
              className="!py-3 !w-[48px] !text-base w-full rounded-xl flex flex-row items-center gap-2 whitespace-nowrap"
              target="_blank"
            >
              {/* Edit */}
              <SIcon type={SIconType.Edit} size={18} />
            </SButton>
          ) : (
            ""
          )}

          <SButton
            variant="juicy"
            className="!py-3 !text-base rounded-xl whitespace-nowrap"
            onClick={async () => {
              try {
                let newPaywall = await duplicatePaywallMutation({
                  id: paywallId,
                  name: `${name || paywall.name} (${isCloningAsTemplate ? `Template` : `Copy`})`,
                  targetApplicationId: applicationId,
                  cloneOptionType,
                })

                const newPaywallRoute = getEditorRouteByPaywall({
                  paywall: newPaywall,
                })!

                console.log("newPaywallRoute", newPaywallRoute)

                if (newPaywall.v4) {
                  const href = newPaywallRoute.href
                  if (href && typeof window !== "undefined") {
                    window.location.href = href
                  }
                } else {
                  router.push(newPaywallRoute)
                }
              } catch (error) {
                console.error(error)
                return {
                  [FORM_ERROR]: error.toString(),
                }
              }
            }}
          >
            {isCloningAsTemplate ? `Use Template →` : `Duplicate →`}
          </SButton>
        </div>
      </div>
    )
  }

  return (
    <>
      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-white">
        {`Duplicate Paywall`}
      </Dialog.Title>
      <div className="mt-2">
        <div className={`mt-4 flex flex-col items-center`}></div>
      </div>
      {isAdminUser && (
        <DuplicatePaywallButton
          onClick={() => {
            invoke(getPaywallPreviewToken, { applicationId, paywallId }).then(({ token }) => {
              // Redirect to localhost editor
              const route = `http://localhost:3000/admin/download-paywall?paywall_id=${paywallId}&sat=${token}`
              window.open(route, "_blank")
            })
          }}
        >
          <div>{`In local dev`}</div>
          <div>
            <IoArrowForward />
          </div>
        </DuplicatePaywallButton>
      )}
      <DuplicatePaywallButton
        onClick={() => {
          router.push(
            Routes.DuplicatePaywallPage({
              applicationId: applicationId,
              paywallId,
              name: `${name} (from: ${application?.name})`,
            })
          )
        }}
      >
        <div>{`In this application`}</div>
        <div>
          <IoArrowForward />
        </div>
      </DuplicatePaywallButton>
      {filteredApplications.map((app) => (
        <DuplicatePaywallButton
          key={app.id}
          onClick={() => {
            router.push(
              Routes.DuplicatePaywallPage({
                applicationId: app.id,
                paywallId,
                name: `${name} (from: ${application?.name})`,
              })
            )
          }}
        >
          <div>
            {`In `}
            {`${app.name}`}
          </div>
          <div>
            <IoArrowForward />
          </div>
        </DuplicatePaywallButton>
      ))}
    </>
  )
}

export const PaywallContainer = ({ paywall }) => {
  const variables = usePaywallVariables({
    applicationId: paywall?.applicationId,
    productPreviewPayload: {
      primary: {
        identifier: paywall.primaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
      secondary: {
        identifier: paywall.secondaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
      tertiary: {
        identifier: paywall.tertiaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
    },
  })

  let productsWithTrialCount = [variables.primary, variables.secondary, variables.tertiary]
    .map((el) => el["trialPeriodDays"])
    .filter((el) => el !== undefined && el != "0").length

  const localizedSubs = useLocalizedSubstitutions({
    applicationId: paywall?.applicationId,
    rawSubs: paywall?.substitutions,
  })
  return (
    <div className="mx-auto">
      <PaywallPreview
        previewUrl={paywall.url}
        // these subs will get translated using the "paywallPreviewLanguage" (default "en" )
        subs={localizedSubs}
        viewFreeTrial={productsWithTrialCount > 0}
        maxWidth={300}
        deviceColor={"hsl(218,14%,30%)"}
        showNotch={false}
        backgroundColor={paywall.backgroundColorHex}
        pageStyles={paywall.pageStyles}
        variables={variables}
        paywall={paywall}
      />
    </div>
  )
}

const DuplicatePaywallAction: FC<{
  paywallId: number
  name?: string
  renderButton?: OpenCloseRender
  cloneOptionType?: CloneOptionType
  showPreview?: boolean
}> = ({
  paywallId,
  name,
  renderButton = defaultRenderButton,
  cloneOptionType = CloneOptionType.AsDuplicate,
  showPreview = false,
}) => {
  return (
    <ModalOverlay
      includeX={showPreview}
      size="sm"
      renderButton={({ open, ...rest }) => {
        return renderButton({ open, ...rest })
      }}
      render={() => {
        return (
          <DropdownButtons
            name={name}
            paywallId={paywallId}
            showPreview={showPreview}
            cloneOptionType={cloneOptionType}
          />
        )
      }}
    />
  )
}

export default DuplicatePaywallAction
